import type { DateKey } from './date';
import { addDays, dateRange } from './date';
import type { PrayerId, QualityKey } from './prayer';
import { PRAYERS, QUALITIES } from './prayer';
import type { DayRecord } from './streaks';
import { dayMeets, markFor } from './streaks';

/** Seven days ending on the anchor, oldest first. */
export const WINDOW_DAYS = 7;

/**
 * The rolling ranges the statistics can be read over, in days.
 *
 * The grid's seven days are one reading of a record and a deliberately short
 * one — a week is what a child can hold in mind, and it is the length the
 * pager steps in. It is also too short to be a statistic: seven days say
 * nothing about whether Jamā‘ah is a habit or a good week. These are the same
 * question asked of a fortnight, a month and a season, and the shortest of
 * them is the grid's own week so the two readings can be compared.
 */
export const RANGES = [7, 14, 30, 90] as const;
export type Range = (typeof RANGES)[number];

/**
 * A month, not the week above it. Opening the report on the seven days it
 * already draws in full would make the section a second copy of the grid's
 * counts; a month is the shortest range that says something the grid cannot.
 */
export const DEFAULT_RANGE: Range = 30;

/** A range out of the URL, falling back to the default rather than failing. */
export function rangeFrom(value: string | null | undefined): Range {
  const days = Number(value);
  return (RANGES as readonly number[]).includes(days) ? (days as Range) : DEFAULT_RANGE;
}

/**
 * A rolling seven days rather than a calendar week. A calendar week would
 * force a choice of first day — Monday, Saturday and Sunday are all normal
 * depending on where a family lives — and "your last seven days" needs no such
 * decision. Recorded as an open question in docs/DESIGN.md.
 *
 * Rolling is what lets one function serve both readings: the grid asks for the
 * seven days ending on its anchor, and the statistics for the last thirty
 * ending today.
 */
export function windowFor(
  anchor: DateKey,
  length: number = WINDOW_DAYS,
): { from: DateKey; to: DateKey } {
  return { from: addDays(anchor, -(length - 1)), to: anchor };
}

export interface ReportCell {
  date: DateKey;
  prayer: PrayerId;
  /** True when the prayer's own day has passed; drives blank vs not-yet. */
  past: boolean;
}

export interface ReportSummary {
  from: DateKey;
  to: DateKey;
  dates: DateKey[];
  /** Prayers meeting each quality across the window. */
  counts: Record<QualityKey, number>;
  /** Prayers in the window: seven days of five. */
  total: number;
  /** Days where all five were prayed. */
  completeDays: number;
  /** Prayers where all four qualities were held. */
  perfectPrayers: number;
  /** Longest run of complete days inside the window. */
  bestRun: number;
}

/** The same figures over any rolling range: the last seven days, or ninety. */
export function summariseRange(days: DayRecord[], anchor: DateKey, length: number): ReportSummary {
  const { from, to } = windowFor(anchor, length);
  const dates = dateRange(from, to);
  const byDate = new Map(days.map((d) => [d.date, d]));

  const counts: Record<QualityKey, number> = {
    prayed: 0,
    onTime: 0,
    jamaah: 0,
    takbirah: 0,
  };
  let perfectPrayers = 0;
  let completeDays = 0;
  let bestRun = 0;
  let run = 0;

  for (const date of dates) {
    const day = byDate.get(date);
    for (const prayer of PRAYERS) {
      const mark = markFor(day, prayer);
      for (const q of QUALITIES) if (mark[q.key]) counts[q.key] += 1;
      if (mark.prayed && mark.onTime && mark.jamaah && mark.takbirah) perfectPrayers += 1;
    }
    if (dayMeets(day, 'prayed')) {
      completeDays += 1;
      run += 1;
      bestRun = Math.max(bestRun, run);
    } else {
      run = 0;
    }
  }

  return {
    from,
    to,
    dates,
    counts,
    total: dates.length * PRAYERS.length,
    completeDays,
    perfectPrayers,
    bestRun,
  };
}

/** The grid's seven days, ending on its anchor. */
export function summariseWindow(days: DayRecord[], anchor: DateKey): ReportSummary {
  return summariseRange(days, anchor, WINDOW_DAYS);
}

/** Can the window move forward? Never past today. */
export function canGoForward(anchor: DateKey, today: DateKey): boolean {
  return anchor < today;
}

export function nextAnchor(anchor: DateKey, today: DateKey): DateKey {
  const candidate = addDays(anchor, WINDOW_DAYS);
  return candidate > today ? today : candidate;
}

export function previousAnchor(anchor: DateKey): DateKey {
  return addDays(anchor, -WINDOW_DAYS);
}
